import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  Copy,
  Check,
  AlertCircle,
  Calendar,
  ExternalLink,
  DollarSign,
  FileText,
  User,
  Link as LinkIcon,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { revSyncService, ContractDetail } from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";

const ContractDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToastContext();
  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("Contract ID is required");
      setIsLoading(false);
      return;
    }

    fetchContract(id);
  }, [id]);

  const fetchContract = async (contractId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await revSyncService.getContract(contractId);
      setContract(response.data);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load contract";

      if (err?.response?.status === 404) {
        setError("Contract not found");
      } else if (err?.response?.status === 401) {
        setError("Unauthorized - please log in again");
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast("success", "Copied to clipboard");
    } catch (error) {
      showToast("error", "Failed to copy");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30";
      case "past":
        return "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/50";
      default:
        return "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/50";
    }
  };

  const getContractTypeBadge = (contract: ContractDetail) => {
    // Show "Renewal" if dealType is "Renewal"
    if (contract.dealType === "Renewal") {
      return {
        label: "Renewal",
        color: "bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/30",
      };
    }
    // Otherwise show contractType
    if (contract.contractType === "new") {
      return {
        label: "New Business",
        color: "bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30",
      };
    }
    if (contract.contractType === "existing") {
      return {
        label: "Existing Business",
        color: "bg-secondary/10 dark:bg-secondary/20 text-secondary border border-secondary/30 dark:border-secondary/40",
      };
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark font-medium">
            Loading contract...
          </p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-4">
        <div className="text-center max-w-md">
          <p className="text-xl font-semibold text-text-primary dark:text-text-primaryDark mb-2">
            {error || "Contract not found"}
          </p>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark leading-relaxed">
            {error === "Contract not found"
              ? "The contract you're looking for doesn't exist or has been removed."
              : "Please try again or contact support if the problem persists."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-32 py-10">
      {/* Slack-style Header */}
      <div className="mb-10">
        {/* Title Section - Slack Style */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-5">
            <div className="flex-1">
              <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2 tracking-tight">
                {contract.contractName ||
                  contract.customer?.name ||
                  contract.customerName ||
                  "Contract"}
              </h1>
              <button
                onClick={() =>
                  copyToClipboard(contract.id, `contract-header-${contract.id}`)
                }
                className="text-xs text-text-secondary dark:text-text-secondaryDark font-mono hover:text-text-primary dark:hover:text-text-primaryDark transition-colors"
              >
                {contract.id}
              </button>
            </div>
          </div>

          {/* Status Pills - Slack Style */}
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${getStatusColor(
                contract.status
              )}`}
            >
              {contract.status}
            </span>
            {(() => {
              const typeBadge = getContractTypeBadge(contract);
              return typeBadge ? (
                <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${typeBadge.color}`}>
                  {typeBadge.label}
                </span>
              ) : null;
            })()}
            {contract.billingCycle && (
              <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/30 capitalize">
                {contract.billingCycle}
              </span>
            )}
            {contract.isHistorical && (
              <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700/50">
                Historical
              </span>
            )}
            {contract.priorContractId && (
              <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/30">
                Renewal
              </span>
            )}
            {contract.linkedContracts && contract.linkedContracts.length > 0 && (
              <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30">
                {contract.linkedContracts.length} Renewal{contract.linkedContracts.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
      </div>


      {/* Financial Metrics - Slack Card Style */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-6">
          <DollarSign className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
            Financial Metrics
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-6 border border-divider/30 dark:border-divider-dark/30 hover:border-divider/50 dark:hover:border-divider-dark/50 transition-all hover:shadow-md">
            <div className="text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-3 uppercase tracking-wider">
              Monthly Recurring Revenue
            </div>
            <div className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
              {formatCurrency(contract.mrr)}
            </div>
          </div>
          <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-6 border border-divider/30 dark:border-divider-dark/30 hover:border-divider/50 dark:hover:border-divider-dark/50 transition-all hover:shadow-md">
            <div className="text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-3 uppercase tracking-wider">
              Annual Recurring Revenue
            </div>
            <div className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
              {formatCurrency(contract.arr)}
            </div>
          </div>
          <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-6 border border-divider/30 dark:border-divider-dark/30 hover:border-divider/50 dark:hover:border-divider-dark/50 transition-all hover:shadow-md">
            <div className="text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-3 uppercase tracking-wider">
              Total Contract Value
            </div>
            <div className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
              {contract.tcv !== null && contract.tcv !== undefined
                ? formatCurrency(contract.tcv)
                : "—"}
            </div>
          </div>
          <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-6 border border-divider/30 dark:border-divider-dark/30 hover:border-divider/50 dark:hover:border-divider-dark/50 transition-all hover:shadow-md">
            <div className="text-[11px] font-medium text-text-secondary dark:text-text-secondaryDark mb-3 uppercase tracking-wider">
              Annual Contract Value
            </div>
            <div className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
              {contract.acv !== null && contract.acv !== undefined
                ? formatCurrency(contract.acv)
                : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Visualization - Slack Card */}
      <div className="mb-8 bg-background-panel dark:bg-background-panelDark rounded-2xl p-8 border border-divider/30 dark:border-divider-dark/30">
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-1">
            Revenue Overview
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            Monthly and annual recurring revenue comparison
          </p>
        </div>

        {/* MRR vs ARR Bar Chart */}
        <div className="mb-10">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  {
                    name: "MRR",
                    value: contract.mrr,
                    color: "#0077ED",
                  },
                  {
                    name: "ARR",
                    value: contract.arr,
                    color: "#8B5CF6",
                  },
                ]}
                margin={{
                  top: 20,
                  right: 20,
                  left: 0,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="currentColor"
                  className="stroke-divider dark:stroke-divider-dark opacity-20"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  className="text-text-secondary dark:text-text-secondaryDark"
                  axisLine={false}
                  tickLine={false}
                  fontSize={13}
                  fontWeight={500}
                />
                <YAxis
                  className="text-text-secondary dark:text-text-secondaryDark"
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                  tickFormatter={(value) => {
                    if (value >= 1000000)
                      return `$${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
                    return `$${value}`;
                  }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-xl p-3 shadow-xl">
                          <p className="text-text-primary dark:text-text-primaryDark text-xs font-medium mb-1">
                            {payload[0].name}
                          </p>
                          <p className="text-text-primary dark:text-text-primaryDark text-base font-semibold">
                            {formatCurrency(payload[0].value as number)}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                  cursor={{ fill: "rgba(0, 0, 0, 0.03)" }}
                />
                <Bar dataKey="value" radius={[12, 12, 0, 0]}>
                  {[
                    {
                      name: "MRR",
                      value: contract.mrr,
                      color: "#0077ED",
                    },
                    {
                      name: "ARR",
                      value: contract.arr,
                      color: "#8B5CF6",
                    },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Contract Timeline */}
        {contract.startDate && (
          <div className="pt-8 border-t border-divider/30 dark:border-divider-dark/30">
            <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-6">
              Contract Timeline
            </h3>
            {(() => {
              const startDate = new Date(contract.startDate);
              const endDate = contract.endDate
                ? new Date(contract.endDate)
                : null;
              const now = new Date();
              const isActive = contract.status === "active";

              let progress = 0;
              let daysElapsed = 0;
              let totalDays = 0;

              if (endDate) {
                totalDays = Math.ceil(
                  (endDate.getTime() - startDate.getTime()) /
                    (1000 * 60 * 60 * 24)
                );
                if (isActive && now >= startDate && now <= endDate) {
                  daysElapsed = Math.ceil(
                    (now.getTime() - startDate.getTime()) /
                      (1000 * 60 * 60 * 24)
                  );
                  progress = Math.min(
                    100,
                    Math.max(0, (daysElapsed / totalDays) * 100)
                  );
                } else if (now > endDate) {
                  progress = 100;
                  daysElapsed = totalDays;
                }
              }

              const formatShortDate = (date: Date) => {
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year:
                    date.getFullYear() !== now.getFullYear()
                      ? "numeric"
                      : undefined,
                });
              };

              return (
                <div className="space-y-4">
                  <div className="relative">
                    <div className="h-3 bg-background-default dark:bg-background-dark rounded-full overflow-hidden">
                      {endDate && (
                        <div
                          className={`h-full rounded-full transition-all ${
                            isActive && progress < 100
                              ? "bg-gradient-to-r from-blue-500 to-purple-500"
                              : progress === 100
                              ? "bg-emerald-500"
                              : "bg-slate-300 dark:bg-slate-600"
                          }`}
                          style={{
                            width: `${Math.min(100, Math.max(0, progress))}%`,
                          }}
                        />
                      )}
                      {!endDate && isActive && (
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                          style={{ width: "100%" }}
                        />
                      )}
                    </div>
                    {isActive && endDate && progress < 100 && progress > 0 && (
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-background-paperDark border-2 border-blue-500 rounded-full shadow-lg"
                        style={{
                          left: `${progress}%`,
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex flex-col">
                      <span className="text-text-secondary dark:text-text-secondaryDark font-medium text-xs mb-1">
                        Start
                      </span>
                      <span className="text-text-primary dark:text-text-primaryDark font-medium">
                        {formatShortDate(startDate)}
                      </span>
                    </div>
                    {isActive && endDate && progress > 0 && progress < 100 && (
                      <div className="flex flex-col items-center">
                        <span className="text-text-secondary dark:text-text-secondaryDark font-medium text-xs mb-1">
                          Progress
                        </span>
                        <span className="text-text-primary dark:text-text-primaryDark font-semibold">
                          {Math.round(progress)}%
                        </span>
                        <span className="text-text-secondary dark:text-text-secondaryDark text-xs mt-0.5">
                          {daysElapsed} of {totalDays} days
                        </span>
                      </div>
                    )}
                    {endDate && (
                      <div className="flex flex-col items-end">
                        <span className="text-text-secondary dark:text-text-secondaryDark font-medium text-xs mb-1">
                          End
                        </span>
                        <span className="text-text-primary dark:text-text-primaryDark font-medium">
                          {formatShortDate(endDate)}
                        </span>
                      </div>
                    )}
                    {!endDate && (
                      <div className="flex flex-col items-end">
                        <span className="text-text-secondary dark:text-text-secondaryDark font-medium text-xs mb-1">
                          Status
                        </span>
                        <span className="text-text-primary dark:text-text-primaryDark font-medium">
                          Ongoing
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Main Content Grid - Slack Style */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Contract Information */}
        <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-8 border border-divider/30 dark:border-divider-dark/30">
          <div className="flex items-center gap-2 mb-8">
            <Calendar className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
              Contract Information
            </h2>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-4 uppercase tracking-wider">
                Contract Period
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                  <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                    Start Date
                  </span>
                  <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                    {formatDate(contract.startDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                  <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                    End Date
                  </span>
                  <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                    {formatDate(contract.endDate) || "Ongoing"}
                  </span>
                </div>
                {contract.subscriptionStartDate && (
                  <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                    <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                      Subscription Start
                    </span>
                    <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      {formatDate(contract.subscriptionStartDate)}
                    </span>
                  </div>
                )}
                {contract.subscriptionEndDate && (
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                      Subscription End
                    </span>
                    <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                      {formatDate(contract.subscriptionEndDate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {(contract.productName ||
              contract.quantity !== null ||
              contract.paymentTerms ||
              contract.dealOwnerEmail) && (
              <div className="pt-6 border-t border-divider/30 dark:border-divider-dark/30">
                <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-4 uppercase tracking-wider">
                  Additional Details
                </h3>
                <div className="space-y-3">
                  {contract.productName && (
                    <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                      <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                        Product
                      </span>
                      <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                        {contract.productName}
                      </span>
                    </div>
                  )}
                  {contract.quantity !== null &&
                    contract.quantity !== undefined && (
                      <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                        <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                          Quantity
                        </span>
                        <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                          {contract.quantity}
                        </span>
                      </div>
                    )}
                  {contract.paymentTerms && (
                    <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                      <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                        Payment Terms
                      </span>
                      <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                        {contract.paymentTerms}
                      </span>
                    </div>
                  )}
                  {contract.dealOwnerEmail && (
                    <div className="flex items-center justify-between py-2.5">
                      <span className="text-sm font-medium text-text-secondary dark:text-text-secondaryDark">
                        Deal Owner
                      </span>
                      <a
                        href={`mailto:${contract.dealOwnerEmail}`}
                        className="text-sm font-semibold text-secondary hover:opacity-80 transition-opacity"
                      >
                        {contract.dealOwnerEmail}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Information */}
        <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-8 border border-divider/30 dark:border-divider-dark/30">
          <div className="flex items-center gap-2 mb-8">
            <User className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
            <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
              Customer
            </h2>
          </div>
          {contract.customer ? (
            <div className="space-y-6">
              {contract.customer.name && (
                <div>
                  <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
                    Name
                  </h3>
                  <p className="text-base font-semibold text-text-primary dark:text-text-primaryDark">
                    {contract.customer.name}
                  </p>
                </div>
              )}
              {contract.customer.email && (
                <div>
                  <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
                    Email
                  </h3>
                  <a
                    href={`mailto:${contract.customer.email}`}
                    className="text-base font-medium text-secondary hover:opacity-80 transition-opacity inline-flex items-center gap-2"
                  >
                    {contract.customer.email}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
              {(contract.customer.hubspotContactId ||
                contract.customer.hubspotCompanyId) && (
                <div className="pt-6 border-t border-divider/30 dark:border-divider-dark/30">
                  <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-4 uppercase tracking-wider">
                    HubSpot IDs
                  </h3>
                  <div className="space-y-3">
                    {contract.customer.hubspotContactId && (
                      <div className="flex items-center justify-between py-2.5 border-b border-divider/20 dark:border-divider-dark/20">
                        <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                          Contact
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              contract.customer!.hubspotContactId!,
                              `customer-contact-${contract.id}`
                            )
                          }
                          className="text-xs font-mono text-text-primary dark:text-text-primaryDark hover:text-secondary transition-colors"
                        >
                          {contract.customer.hubspotContactId}
                        </button>
                      </div>
                    )}
                    {contract.customer.hubspotCompanyId && (
                      <div className="flex items-center justify-between py-2.5">
                        <span className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark">
                          Company
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              contract.customer!.hubspotCompanyId!,
                              `customer-company-${contract.id}`
                            )
                          }
                          className="text-xs font-mono text-text-primary dark:text-text-primaryDark hover:text-secondary transition-colors"
                        >
                          {contract.customer.hubspotCompanyId}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                {contract.customerName || contract.customerEmail
                  ? `Legacy: ${
                      contract.customerName ||
                      contract.customerEmail ||
                      "No customer information"
                    }`
                  : "No customer information available"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Integrations - Slack Card Style */}
      <div className="mb-8 bg-background-panel dark:bg-background-panelDark rounded-2xl p-8 border border-divider/30 dark:border-divider-dark/30">
        <div className="flex items-center gap-2 mb-8">
          <LinkIcon className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark">
            Integrations
          </h2>
        </div>
        <div className="space-y-4">
          {contract.hubspotDealId && (
            <div className="flex items-center justify-between py-4 border-b border-divider/20 dark:border-divider-dark/20 last:border-0">
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
                  HubSpot Deal
                </h3>
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.hubspotDealId!,
                      `hubspot-deal-${contract.id}`
                    )
                  }
                  className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                >
                  {contract.hubspotDealId}
                </button>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                {contract.urls?.hubspot?.deal && (
                  <a
                    href={contract.urls.hubspot.deal}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                    title="Open in HubSpot"
                  >
                    <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                  </a>
                )}
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.hubspotDealId!,
                      `hubspot-deal-${contract.id}`
                    )
                  }
                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                  title="Copy ID"
                >
                  {copiedId === `hubspot-deal-${contract.id}` ? (
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                  )}
                </button>
              </div>
            </div>
          )}

          {contract.stripeSubscriptionId && (
            <div className="flex items-center justify-between py-4 border-b border-divider/20 dark:border-divider-dark/20 last:border-0">
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
                  Stripe Subscription
                </h3>
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.stripeSubscriptionId!,
                      `stripe-${contract.id}`
                    )
                  }
                  className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                >
                  {contract.stripeSubscriptionId}
                </button>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                {contract.urls?.stripe?.subscription && (
                  <a
                    href={contract.urls.stripe.subscription}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                    title="Open in Stripe"
                  >
                    <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-[#8B5CF6]" />
                  </a>
                )}
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.stripeSubscriptionId!,
                      `stripe-${contract.id}`
                    )
                  }
                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                  title="Copy ID"
                >
                  {copiedId === `stripe-${contract.id}` ? (
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                  )}
                </button>
              </div>
            </div>
          )}

          {contract.stripeCustomerId && (
            <div className="flex items-center justify-between py-4">
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
                  Stripe Customer
                </h3>
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.stripeCustomerId!,
                      `stripe-customer-${contract.id}`
                    )
                  }
                  className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                >
                  {contract.stripeCustomerId}
                </button>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                {contract.urls?.stripe?.customer && (
                  <a
                    href={contract.urls.stripe.customer}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                    title="Open in Stripe"
                  >
                    <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-[#8B5CF6]" />
                  </a>
                )}
                <button
                  onClick={() =>
                    copyToClipboard(
                      contract.stripeCustomerId!,
                      `stripe-customer-${contract.id}`
                    )
                  }
                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors"
                  title="Copy ID"
                >
                  {copiedId === `stripe-customer-${contract.id}` ? (
                    <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contract ID - Slack Card */}
      <div className="bg-background-panel dark:bg-background-panelDark rounded-2xl p-8 border border-divider/30 dark:border-divider-dark/30">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wider">
              Contract ID
            </h3>
            <button
              onClick={() =>
                copyToClipboard(contract.id, `contract-${contract.id}`)
              }
              className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
            >
              {contract.id}
            </button>
          </div>
          <button
            onClick={() =>
              copyToClipboard(contract.id, `contract-${contract.id}`)
            }
            className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-xl transition-colors ml-4 flex-shrink-0"
            title="Copy Contract ID"
          >
            {copiedId === `contract-${contract.id}` ? (
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractDetailPage;
